/* =========================================================================
   BLINDPAST — scroll choreography, liquid glass, and the bag.
   ========================================================================= */
(function () {
  'use strict';

  var root = document.documentElement;
  var REDUCED = root.getAttribute('data-reduced') === 'true';
  var FINE = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  var data = { colours: [], routes: {}, moneyFormat: '${{amount}}' };
  var dataEl = document.getElementById('pdp-data');
  if (dataEl) {
    try {
      data = JSON.parse(dataEl.textContent);
    } catch (e) {
      if (window.console) console.error('[blindpast] bad pdp-data', e);
    }
  }

  function money(cents) {
    return (data.moneyFormat || '${{amount}}').replace(
      /\{\{\s*amount\s*\}\}/,
      (Number(cents) / 100).toFixed(2)
    );
  }

  /* -----------------------------------------------------------------------
     TRUE VIEWPORT WIDTH — some iOS setups lay the page out wider than the
     screen (everything anchors left and spills off the right edge). CSS
     units (%, vw) inflate along with it, so the only trustworthy number is
     the visual viewport. Published as --vvw; the fold's glass pieces are
     max-width-capped against it. Pinch-zoom (scale > 1) is ignored so
     zooming in never shrinks the layout.
     ----------------------------------------------------------------------- */
  (function trueWidth() {
    function set() {
      var vv = window.visualViewport;
      var w = window.innerWidth;
      if (vv && vv.scale <= 1.01 && vv.width) w = Math.min(w, vv.width);
      // Ignore nonsense readings (hidden panes report 0) — never shrink
      // the layout below a real phone width.
      if (w >= 240) root.style.setProperty('--vvw', Math.round(w) + 'px');
    }
    set();
    window.addEventListener('resize', set);
    window.addEventListener('orientationchange', set);
    if (window.visualViewport) window.visualViewport.addEventListener('resize', set);
  })();

  /* --devw: the PHYSICAL screen width in points. Every viewport number can
     be inflated by iOS layout bugs; screen.width cannot. On phones in
     portrait the fold's glass pieces take their width from this, so the
     right gap always equals the left gap no matter what the viewport does. */
  (function deviceWidth() {
    function set() {
      var portrait = window.matchMedia('(orientation: portrait)').matches;
      var sw = portrait
        ? Math.min(screen.width, screen.height)
        : Math.max(screen.width, screen.height);
      if (sw >= 240) root.style.setProperty('--devw', sw + 'px');
    }
    set();
    window.addEventListener('orientationchange', function () { setTimeout(set, 60); });
  })();

  /* Open the page with #debug in the URL to see exactly what this device is
     doing to the layout — widths, scales, and any element crossing the right
     edge. Renders nothing without the hash. */
  if (window.location.hash.indexOf('debug') > -1) {
    window.addEventListener('load', function () {
      setTimeout(function () {
        var doc = document.documentElement;
        var vv = window.visualViewport;
        var lines = [
          'BLINDPAST v19',
          'innerW ' + window.innerWidth,
          'clientW ' + doc.clientWidth,
          'scrollW ' + doc.scrollWidth,
          'screen ' + screen.width + 'x' + screen.height,
          'vv ' + (vv ? Math.round(vv.width) + ' @' + vv.scale.toFixed(3) : 'n/a'),
          'dpr ' + window.devicePixelRatio,
          '--vvw ' + getComputedStyle(root).getPropertyValue('--vvw'),
          '--devw ' + getComputedStyle(root).getPropertyValue('--devw')
        ];
        var lim = doc.clientWidth + 1;
        var els = document.querySelectorAll('body *');
        var count = 0;
        for (var i = 0; i < els.length && count < 6; i++) {
          var cs = getComputedStyle(els[i]);
          if (cs.visibility === 'hidden' || cs.display === 'none') continue;
          // The gallery strip scrolls sideways by design — not a culprit.
          if (els[i].closest && els[i].closest('.gal')) continue;
          var r = els[i].getBoundingClientRect();
          if (r.width > 0 && r.right > lim) {
            count++;
            lines.push('OVER: ' + els[i].tagName.toLowerCase() +
              '.' + String(els[i].className).split(' ')[0].slice(0, 18) +
              ' r=' + Math.round(r.right));
          }
        }
        if (!count) lines.push('no elements past right edge');
        var box = document.createElement('div');
        box.style.cssText = 'position:fixed;top:70px;left:8px;z-index:99999;' +
          'background:#000;color:#0f0;font:11px/1.5 monospace;padding:8px 10px;' +
          'border:1px solid #0f0;border-radius:6px;max-width:80vw;white-space:pre;';
        box.textContent = lines.join('\n');
        document.body.appendChild(box);
      }, 800);
    });
  }

  /* One rAF loop. A throwing task is dropped rather than killing the loop and
     freezing every animation on the page. */
  var tasks = [];
  function tick(now) {
    for (var i = tasks.length - 1; i >= 0; i--) {
      try {
        if (tasks[i](now) === false) tasks.splice(i, 1);
      } catch (err) {
        tasks.splice(i, 1);
        if (window.console) console.error('[blindpast] frame task removed:', err);
      }
    }
    window.requestAnimationFrame(tick);
  }
  window.requestAnimationFrame(tick);

  /* ---------------------------------------------------------------------
     SCROLL REVEAL — rises out of depth, un-blurs, settles.
     --------------------------------------------------------------------- */

  (function reveal() {
    var els = [].slice.call(document.querySelectorAll('[data-rev]'));
    if (!els.length) return;

    if (REDUCED || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-in'); });
      return;
    }

    var io = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (en) {
          if (!en.isIntersecting) return;
          en.target.classList.add('is-in');
          io.unobserve(en.target); // reveal once; re-triggering on scroll-up is noise
        });
      },
      { rootMargin: '0px 0px -12% 0px', threshold: 0.08 }
    );
    els.forEach(function (el) { io.observe(el); });
  })();

  /* ---------------------------------------------------------------------
     PARALLAX — the campaign image drifts against its frame.
     --------------------------------------------------------------------- */

  if (!REDUCED) {
    (function parallax() {
      var host = document.querySelector('[data-parallax]');
      var img = document.querySelector('[data-parallax-img]');
      if (!host || !img) return;

      var visible = false;
      var io = new IntersectionObserver(function (e) { visible = e[0].isIntersecting; });
      io.observe(host);

      tasks.push(function () {
        if (!visible || document.hidden) return true;
        var r = host.getBoundingClientRect();
        // -1 when the section is entering, +1 when leaving.
        var p = (r.top + r.height / 2 - window.innerHeight / 2) / window.innerHeight;
        p = Math.max(-1.2, Math.min(1.2, p));
        img.style.transform = 'scale(1.18) translate3d(0,' + (p * -7).toFixed(2) + '%,0)';
        return true;
      });
    })();
  }


  /* ---------------------------------------------------------------------
     COLOURWAY
     --------------------------------------------------------------------- */

  var swatches = [].slice.call(document.querySelectorAll('.sw, .lswitch__half'));
  var lswitch = document.querySelector('.lswitch');
  var galleries = [].slice.call(document.querySelectorAll('[data-gal]'));
  var teases = [].slice.call(document.querySelectorAll('[data-tease]'));
  var priceEl = document.getElementById('price');
  var stockEl = document.getElementById('stock');
  var nameEl = document.getElementById('colour-name');
  var addBtn = document.getElementById('add');
  var barAdd = document.getElementById('buybar-add');
  var barPrice = document.getElementById('buybar-price');

  var currentColour = null;

  function byName(name) {
    for (var i = 0; i < data.colours.length; i++) {
      if ((data.colours[i].name || '').toUpperCase() === name) return data.colours[i];
    }
    return null;
  }

  function selectColour(name) {
    var c = byName(name);

    swatches.forEach(function (s) {
      s.setAttribute('aria-checked', s.getAttribute('data-colour') === name ? 'true' : 'false');
    });

    galleries.forEach(function (g) {
      if (g.getAttribute('data-gal') === name) g.removeAttribute('hidden');
      else g.setAttribute('hidden', '');
    });

    teases.forEach(function (t) {
      if (t.getAttribute('data-tease') === name) t.removeAttribute('hidden');
      else t.setAttribute('hidden', '');
    });

    currentColour = name;
    root.setAttribute('data-colour', name);
    try { localStorage.setItem('bp-colour', name); } catch (e) {}

    // The checkbox offers whichever pair is NOT selected, so it re-targets
    // on every switch (function is hoisted from the checkout block below).
    if (typeof refreshUpsell === 'function') refreshUpsell();

    // Slide the switch thumb to whichever half is now selected.
    if (lswitch) {
      var picked = lswitch.querySelector('[data-colour="' + name + '"]');
      if (picked) lswitch.setAttribute('data-side', picked.getAttribute('data-side'));
    }

    if (nameEl && c) nameEl.textContent = c.name;
    if (priceEl && c && c.price) priceEl.textContent = money(c.price);

    /* Notify colourways (the ICE drop) swap the whole purchase block for the
       SMS card. The variant exists in Shopify but holds zero stock with a
       DENY policy, so even a hand-built cart call cannot buy it — this swap
       is presentation, the lock is Shopify's. */
    var isNotify = !!(c && c.notify);
    var purchaseEl = document.getElementById('buy-purchase');
    var notifyEl = document.getElementById('buy-notify');
    if (purchaseEl) purchaseEl.hidden = isNotify;
    if (notifyEl) notifyEl.hidden = !isNotify;
    var barEl = document.getElementById('buybar');
    if (barEl) barEl.style.display = isNotify ? 'none' : '';

    if (!isNotify) [addBtn, barAdd].forEach(function (b) {
      if (!b || !c) return;
      b.setAttribute('data-id', c.id);
      if (!c.available || !c.id) {
        b.disabled = true;
        b.textContent = 'Sold out';
      } else {
        b.disabled = false;
        // Keep the PWYW CTA label; only 'Sold out' may overwrite it.
        if (!b.getAttribute('data-label')) b.setAttribute('data-label', b.textContent);
        b.textContent = b.getAttribute('data-label');
      }
    });

    if (stockEl && c) {
      if (isNotify) {
        stockEl.textContent = data.notifyStock || 'Drops soon';
        stockEl.setAttribute('data-state', 'soon');
      } else if (!c.available) {
        stockEl.textContent = 'Sold out';
        stockEl.setAttribute('data-state', 'out');
      } else if (c.qty > 0 && c.qty < 10) {
        stockEl.textContent = 'Only ' + c.qty + ' left';
        stockEl.setAttribute('data-state', 'low');
      } else {
        stockEl.textContent = data.stockLabel || 'In stock';
        stockEl.setAttribute('data-state', 'in');
      }
    }

  }

  swatches.forEach(function (s) {
    s.addEventListener('click', function () {
      selectColour(s.getAttribute('data-colour'));
    });
  });


  /* ---------------------------------------------------------------------
     INFINITE GALLERY — the track holds three identical passes. Whenever the
     scroll position crosses out of the middle copy it is moved back by exactly
     one set width. The pixels either side are identical, so the jump is
     invisible and the strip scrolls forever in both directions with the
     browser's own momentum. No snapping.
     --------------------------------------------------------------------- */

  [].slice.call(document.querySelectorAll('[data-loop]')).forEach(function (gal) {
    var setW = 0;
    var placed = false;

    function measure() {
      // Three passes were rendered, so one set is a third of the track.
      setW = gal.scrollWidth / 3;
      if (setW <= 0) return;
      if (!placed) {
        // Start in the middle copy so there is room to scroll either way.
        gal.scrollLeft = setW;
        placed = true;
      } else {
        // On resize, keep where the visitor is. Mobile browsers fire resize
        // when the address bar hides, and forcing the position here would
        // yank the strip out from under a swipe.
        wrap();
      }
    }

    function wrap() {
      if (setW <= 0) return;
      if (gal.scrollLeft >= setW * 2) gal.scrollLeft -= setW;
      else if (gal.scrollLeft < setW) gal.scrollLeft += setW;
    }

    // Wait for the first tile to have a width before measuring.
    if (document.readyState === 'complete') measure();
    else window.addEventListener('load', measure);
    measure();

    window.addEventListener('resize', measure);
    gal.addEventListener('scroll', wrap, { passive: true });
  });


  /* ---------------------------------------------------------------------
     BEAM — a line of light drawn along the story as you scroll, weaving
     between the blocks, with a glowing head riding the tip. Fully
     scroll-linked: scrubbing back up un-draws it.
     --------------------------------------------------------------------- */

  (function ember() {
    var story = document.querySelector('[data-story]');
    var orb = document.getElementById('ember');
    if (!story || !orb || REDUCED) return;

    var cur = 0;
    var lit = false;

    tasks.push(function (now) {
      if (document.hidden) return true;
      var r = story.getBoundingClientRect();
      var vh = window.innerHeight;
      if (r.bottom < -300 || r.top > vh + 300) return true;

      // 0 as the section enters, 1 as it leaves.
      var p = (vh - r.top) / (r.height + vh * 0.6);
      p = Math.max(0, Math.min(1, p));
      cur += (p - cur) * 0.10;

      var w = orb.offsetWidth || 1;
      // Glide down the section, swaying through six half-turns so it loops
      // around the alternating blocks, with a slow breathing pulse on top.
      var x = (r.width - w) * (0.5 + 0.40 * Math.sin(cur * Math.PI * 6));
      var y = (r.height - w) * cur;
      var s = 1 + 0.06 * Math.sin(now / 900);
      orb.style.transform =
        'translate3d(' + x.toFixed(1) + 'px,' + y.toFixed(1) + 'px,0) scale(' + s.toFixed(3) + ')';

      var wantLit = cur > 0.015 && cur < 0.985;
      if (wantLit !== lit) {
        lit = wantLit;
        orb.classList.toggle('is-lit', lit);
      }
      return true;
    });
  })();

  /* Story images drift gently against the scroll — cheap parallax. */
  (function drift() {
    if (REDUCED) return;
    var els = [].slice.call(document.querySelectorAll('[data-drift]'));
    if (!els.length) return;
    tasks.push(function () {
      if (document.hidden) return true;
      var vh = window.innerHeight;
      for (var i = 0; i < els.length; i++) {
        var r = els[i].getBoundingClientRect();
        if (r.bottom < 0 || r.top > vh) continue;
        if (!els[i].classList.contains('is-in')) continue; // let the reveal own the transform first
        var t = (r.top + r.height / 2 - vh / 2) / vh;
        els[i].style.transform = 'translate3d(0,' + (t * -26).toFixed(1) + 'px,0)';
      }
      return true;
    });
  })();

  /* ---------------------------------------------------------------------
     MOBILE BUY BAR — appears once the main CTA has scrolled away.
     --------------------------------------------------------------------- */

  (function buybar() {
    var bar = document.getElementById('buybar');
    if (!bar) return;

    // Show it once the visitor is past the first half of the opening screen.
    tasks.push(function () {
      if (document.hidden) return true;
      bar.classList.toggle('is-up', window.scrollY > window.innerHeight * 1.35);
      return true;
    });
  })();

  /* ---------------------------------------------------------------------
     BAG
     --------------------------------------------------------------------- */

  /* Scroll lock for overlays. iOS Safari ignores overflow:hidden on body for
     touch, so the body is pinned in place and restored on unlock. */
  var lockY = 0, locks = 0;
  function lockScroll() {
    if (locks++ > 0) return;
    lockY = window.scrollY || 0;
    document.body.classList.add('is-locked');
    document.body.style.top = (-lockY) + 'px';
  }
  function unlockScroll() {
    if (locks === 0) return;
    if (--locks > 0) return;
    document.body.classList.remove('is-locked');
    document.body.style.top = '';
    window.scrollTo(0, lockY);
  }

  var drawer = document.getElementById('cart');
  var lines = document.getElementById('cart-lines');
  var totalEl = document.getElementById('cart-total');
  var countEl = document.getElementById('cart-count');
  var checkout = document.getElementById('checkout');
  var openBtn = document.getElementById('cart-open');
  var lastFocus = null;

  function onKey(e) {
    if (e.key === 'Escape') return close();
    if (e.key !== 'Tab' || !drawer) return;
    var f = drawer.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])');
    if (!f.length) return;
    var first = f[0], last = f[f.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function open() {
    if (!drawer) return;
    lastFocus = document.activeElement;
    drawer.classList.add('is-open');
    drawer.setAttribute('aria-hidden', 'false');
    lockScroll();
    var c = drawer.querySelector('.linkbtn');
    if (c) c.focus();
    document.addEventListener('keydown', onKey);
  }

  function close() {
    if (!drawer) return;
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
    unlockScroll();
    document.removeEventListener('keydown', onKey);
    if (lastFocus) lastFocus.focus();
  }

  if (drawer) {
    drawer.querySelectorAll('[data-close]').forEach(function (b) {
      b.addEventListener('click', close);
    });
  }
  if (openBtn) openBtn.addEventListener('click', function () { render().then(open); });

  /* The bag now shows exactly what the customer bought: one line per pair,
     already priced at the amount they chose. The $1 top-up product is gone. */

  function changeLine(key, qty) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: qty })
    });
  }

  var qtyBusy = false;
  function setQty(key, newQty) {
    if (qtyBusy) return;
    qtyBusy = true;
    changeLine(key, Math.max(0, newQty))
      .then(function () { qtyBusy = false; return render(); })
      .catch(function () { qtyBusy = false; render(); });
  }

  function render() {
    return fetch((data.routes.cart || '/cart') + '.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (c) {
        if (countEl) countEl.textContent = c.item_count;
        if (totalEl) totalEl.textContent = money(c.total_price);
        if (checkout) checkout.disabled = c.item_count === 0;
        renderUps(c);
        if (!lines) return c;

        lines.innerHTML = '';
        if (!c.items.length) {
          var p = document.createElement('p');
          p.className = 'lbl';
          p.textContent = 'Your bag is empty.';
          lines.appendChild(p);
          return c;
        }

        c.items.forEach(function (it) {
          var row = document.createElement('div');
          row.className = 'line';
          var img = document.createElement('img');
          img.src = it.image || '';
          img.alt = '';
          var mid = document.createElement('div');
          var t = document.createElement('div');
          t.className = 'line__t';
          t.textContent = it.product_title;
          var v = document.createElement('p');
          v.className = 'lbl';
          // variant title is "BLACK / $73.50" — show the colour, price shown on the right
          v.textContent = String(it.variant_title || '').split(' / ')[0] || '';
          var qty = document.createElement('div');
          qty.className = 'line__qty';
          var minus = document.createElement('button');
          minus.type = 'button';
          minus.textContent = '\u2212';
          minus.setAttribute('aria-label', 'Decrease quantity');
          var q = document.createElement('span');
          q.textContent = it.quantity;
          var plus = document.createElement('button');
          plus.type = 'button';
          plus.textContent = '+';
          plus.setAttribute('aria-label', 'Increase quantity');
          minus.addEventListener('click', function () { setQty(it.key, it.quantity - 1); });
          plus.addEventListener('click', function () { setQty(it.key, it.quantity + 1); });
          qty.appendChild(minus); qty.appendChild(q); qty.appendChild(plus);
          mid.appendChild(t); mid.appendChild(v); mid.appendChild(qty);
          var right = document.createElement('div');
          right.textContent = it.final_line_price === 0 ? 'FREE' : money(it.final_line_price);
          row.appendChild(img); row.appendChild(mid); row.appendChild(right);
          lines.appendChild(row);
        });
        return c;
      })
      .catch(function () {});
  }

  /* ---------------------------------------------------------------------
     CHECKOUT — fixed price, plus a one-tick second pair.
     The selected colourway's base variant goes in the bag; if the upsell box
     is ticked, the OTHER colourway's discounted variant goes in too. Then
     straight to checkout. No minting, no endpoint, nothing to fail.
     --------------------------------------------------------------------- */

  var note = document.getElementById('buy-note');
  var upsellRow = document.getElementById('upsell-row');
  var upsellBox = document.getElementById('upsell');
  var upsellLbl = document.getElementById('upsell-lbl');
  var upsellPriceEl = document.getElementById('upsell-price');

  function addOnce(items) {
    var url = (data.routes.cartAdd || '/cart/add').replace(/\.js$/, '') + '.js';
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: items })
    }).then(function (r) {
      if (r.ok) return true;
      var status = r.status;
      return r.json().then(
        function (j) { var e = new Error((j && j.description) || 'add failed'); e.status = status; throw e; },
        function () { var e = new Error('add failed'); e.status = status; throw e; }
      );
    });
  }

  /* 429 = Shopify throttling; back off rather than hammer. Anything else is
     a real failure and surfaces to the customer immediately. */
  function addToCart(items, attempt) {
    attempt = attempt || 0;
    var ladder = [1200, 2500, 4000];
    return addOnce(items).catch(function (err) {
      if (err.status !== 429 || attempt >= ladder.length) throw err;
      return new Promise(function (res) { setTimeout(res, ladder[attempt]); })
        .then(function () { return addToCart(items, attempt + 1); });
    });
  }

  /* The pair that ISN'T selected — the one the checkbox offers. Drop
     colourways (ICE) are never offered: nothing to sell yet. */
  function otherColour(name) {
    for (var i = 0; i < data.colours.length; i++) {
      var c = data.colours[i];
      if ((c.name || '').toUpperCase() !== name && !c.notify && c.id) return c;
    }
    return null;
  }

  /* Prefer the discounted second-pair variant; fall back to the full-price
     base variant so the offer degrades to "pricier", never to "broken". */
  function upsellFor(name) {
    var o = otherColour(name);
    if (!o) return null;
    var useDiscount = o.upsellId && o.upsellAvailable;
    return {
      colour: o,
      id: useDiscount ? o.upsellId : o.id,
      price: useDiscount ? o.upsellPrice : o.price,
      available: useDiscount || o.available
    };
  }

  function refreshUpsell() {
    if (!upsellRow) return;
    var cur = (currentColour || '').toUpperCase();
    var me = byName(cur);
    var up = upsellFor(cur);
    if (!me || me.notify || !up || !up.available) { upsellRow.hidden = true; return; }
    var tpl = data.upsellLabel || 'Add the {colour} pair';
    if (upsellLbl) upsellLbl.textContent = tpl.replace('{colour}', (up.colour.name || '').toUpperCase());
    if (upsellPriceEl) upsellPriceEl.textContent = money(up.price);
    upsellRow.hidden = false;
  }

  function checkoutNow(btn) {
    var colour = (currentColour || (data.colours[0] && data.colours[0].name) || '').toUpperCase();
    var base = byName(colour) || data.colours[0];
    if (!base || base.notify || !base.id) return;   // ICE has no checkout path

    var items = [{ id: Number(base.id), quantity: 1 }];
    if (upsellBox && upsellBox.checked) {
      var up = upsellFor(colour);
      if (up && up.available) items.push({ id: Number(up.id), quantity: 1 });
    }

    var label = btn.textContent;
    btn.disabled = true;
    btn.textContent = 'One moment';
    if (note) note.textContent = '';

    addToCart(items)
      .then(function () { window.location.href = data.routes.checkout || '/checkout'; })
      .catch(function (err) {
        btn.disabled = false;
        btn.textContent = label;
        if (note) note.textContent = (err && err.message && /sold out/i.test(err.message))
          ? 'That pair just sold out.'
          : 'Something went wrong — try again.';
      });
  }

  if (addBtn) addBtn.addEventListener('click', function () { checkoutNow(addBtn); });
  if (barAdd) barAdd.addEventListener('click', function () { checkoutNow(barAdd); });

  /* ---------------------------------------------------------------------
     ICE NOTIFY CAPTURE — same wiring as the pre-drop page: try the real SMS
     subscribe (a no-op until Klaviyo SMS is enabled), then always stamp our
     own consent evidence on the profile, tagged as the Ice waitlist. The
     page never trusts a bare 202 — the stamp is the record that matters.
     --------------------------------------------------------------------- */
  (function notifyCapture() {
    var KEY = data.klaviyoKey, LIST = data.klaviyoList, REV = '2026-07-15';
    var dial = document.getElementById('nf-dial');
    var phone = document.getElementById('nf-phone');
    var btn = document.getElementById('nf-submit');
    var note = document.getElementById('nf-note');
    var doneEl = document.getElementById('nf-done');
    if (!dial || !phone || !btn || !KEY) return;

    /* Timezone first — it tracks where someone IS. Language only as backup. */
    (function preselect() {
      var DIAL = { US:'+1', CA:'+1', GB:'+44', IE:'+353', AU:'+61', DE:'+49', FR:'+33',
                   NL:'+31', ES:'+34', IT:'+39', PT:'+351', BE:'+32', AT:'+43', FI:'+358' };
      var TZ = { 'Europe/London':'GB','Europe/Belfast':'GB','Europe/Jersey':'GB',
        'Europe/Guernsey':'GB','Europe/Isle_of_Man':'GB','Europe/Dublin':'IE',
        'Europe/Berlin':'DE','Europe/Busingen':'DE','Europe/Paris':'FR',
        'Europe/Amsterdam':'NL','Europe/Madrid':'ES','Atlantic/Canary':'ES','Africa/Ceuta':'ES',
        'Europe/Rome':'IT','Europe/Lisbon':'PT','Atlantic/Azores':'PT','Atlantic/Madeira':'PT',
        'Europe/Brussels':'BE','Europe/Vienna':'AT','Europe/Helsinki':'FI' };
      function fromZone(tz) {
        if (!tz) return '';
        if (TZ[tz]) return DIAL[TZ[tz]];
        if (/^America\//.test(tz) || tz === 'Pacific/Honolulu') return '+1';
        if (tz.indexOf('Australia/') === 0) return '+61';
        return '';
      }
      function fromLang() {
        var langs = navigator.languages || [navigator.language || ''];
        for (var i = 0; i < langs.length; i++) {
          var part = (langs[i] || '').split('-')[1];
          if (part && DIAL[part.toUpperCase()]) return DIAL[part.toUpperCase()];
        }
        return '';
      }
      var want = '';
      try { want = fromZone(Intl.DateTimeFormat().resolvedOptions().timeZone || ''); } catch (e) {}
      if (!want) { try { want = fromLang(); } catch (e) {} }
      if (!want) want = '+1';
      for (var j = 0; j < dial.options.length; j++) {
        if (dial.options[j].value === want) { dial.selectedIndex = j; break; }
      }
    })();

    /* Tell Meta a lead landed.

       fbq is NOT available here: the Meta pixel runs inside Shopify's web-pixel
       sandbox, so calling fbq() from the theme silently does nothing. The
       supported route is to publish a custom event that a Shopify Custom Pixel
       subscribes to — that pixel has its own fbq and forwards a Lead.

       The event id is generated here so the browser Lead and any future
       server-side (Conversions API) Lead for the same signup deduplicate
       instead of counting twice. */
    function signalLead(e164) {
      try {
        var eventId = 'lead-' + Date.now() + '-' + Math.random().toString(36).slice(2, 10);
        var payload = {
          event_id: eventId,
          source: 'Ice waitlist',
          colour: (currentColour || '').toUpperCase(),
          // Hashing is Meta's job inside the pixel; only the country prefix is
          // passed so the sandbox can set a coarse match signal if wanted.
          country_code: (dial && dial.value) || ''
        };
        if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish('blindpast_sms_signup', payload);
        }
        /* If a real fbq ever exists in this window (e.g. a hand-placed base
           pixel), use it directly too — the shared event_id keeps it from
           double-counting against the sandboxed one. */
        if (typeof window.fbq === 'function') {
          window.fbq('track', 'Lead', { content_name: 'ICE waitlist' }, { eventID: eventId });
        }
      } catch (e) {
        if (window.console) console.warn('[blindpast] lead signal failed', e);
      }
    }

    function post(path, payload) {
      return fetch('https://a.klaviyo.com/client/' + path + '/?company_id=' + encodeURIComponent(KEY), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'revision': REV },
        body: JSON.stringify(payload)
      });
    }

    btn.addEventListener('click', function () {
      note.textContent = '';
      var digits = (phone.value || '').replace(/[^0-9]/g, '').replace(/^0+/, '');
      if (digits.length < 6) {
        note.textContent = 'Enter your mobile number.';
        phone.focus();
        return;
      }
      var e164 = dial.value + digits;
      var label = btn.textContent;
      btn.disabled = true;
      btn.textContent = 'One moment';

      var consentEl = document.getElementById('nf-consent');
      var shown = consentEl ? (consentEl.textContent || '').replace(/\s+/g, ' ').trim().slice(0, 500) : '';

      function stamp() {
        return post('profiles', { data: { type: 'profile', attributes: {
          phone_number: e164,
          properties: {
            'Pre-drop SMS consent': true,
            'Pre-drop consent at': new Date().toISOString(),
            'Pre-drop consent method': 'Typed on product page',
            'Pre-drop consent text': shown,
            'Pre-drop source': 'Ice waitlist'
          }
        } } });
      }

      post('subscriptions', { data: { type: 'subscription', attributes: {
        custom_source: 'Ice waitlist',
        profile: { data: { type: 'profile', attributes: {
          phone_number: e164,
          subscriptions: { sms: { marketing: { consent: 'SUBSCRIBED' } } }
        } } }
      }, relationships: { list: { data: { type: 'list', id: LIST } } } } })
        .then(function (r) {
          if (r.status === 202 || r.ok) return stamp().catch(function () {});
          return r.json().then(function (j) {
            var d = (j && j.errors && j.errors[0] && j.errors[0].detail) || '';
            if (/does not exist|ineligible|invalid|not a valid/i.test(d)) {
              throw { bad: true };
            }
            return stamp();
          });
        })
        .then(function () {
          var f = document.querySelector('#buy-notify .notify__field');
          var c = document.getElementById('nf-consent');
          var h = document.querySelector('#buy-notify .notify__h');
          if (f) f.hidden = true;
          if (c) c.hidden = true;
          if (h) h.hidden = true;
          btn.hidden = true;
          note.textContent = '';
          if (doneEl) doneEl.hidden = false;
          signalLead(e164);
        })
        .catch(function (err) {
          btn.disabled = false;
          btn.textContent = label;
          note.textContent = (err && err.bad)
            ? 'That number doesn\u2019t look right \u2014 check it and try again.'
            : 'Something went wrong. Try again in a moment.';
        });
    });
  })();

  /* Boot on the drop colourway (ICE) — it is the hook the ads point at, so
     every visit opens on it rather than on whatever was last clicked. Driven
     off the notify flag, not a hardcoded name, so it follows the drop if the
     colourway is ever renamed or replaced. Falls back to the remembered
     colour, then the first swatch, if nothing is flagged. */
  if (swatches.length) {
    var lead = '';
    for (var ci = 0; ci < data.colours.length; ci++) {
      if (data.colours[ci].notify) { lead = (data.colours[ci].name || '').toUpperCase(); break; }
    }
    var want = lead || root.getAttribute('data-colour');
    var match = swatches.filter(function (s) {
      return s.getAttribute('data-colour') === want;
    })[0];
    selectColour((match || swatches[0]).getAttribute('data-colour'));
  } else if (data.colours.length) {
    // Single-colour product page: no switch to click, so wire the one variant.
    selectColour((data.colours[0].name || '').toUpperCase());
  }

  /* ---------------------------------------------------------------------
     SHOP — grid tiles open a quick-add sheet; the bag gets upsells.
     Product JSON comes from the shop-grid section (#shop-data), so both
     only exist on the collection page. Elsewhere every function here is a
     no-op.
     --------------------------------------------------------------------- */

  var shop = null;
  var shopEl = document.getElementById('shop-data');
  if (shopEl) {
    try { shop = JSON.parse(shopEl.textContent); } catch (e) {
      if (window.console) console.error('[blindpast] bad shop-data', e);
    }
  }

  function productById(id) {
    if (!shop) return null;
    for (var i = 0; i < shop.products.length; i++) {
      if (String(shop.products[i].id) === String(id)) return shop.products[i];
    }
    return null;
  }

  /* ----- quick-add sheet ------------------------------------------------ */
  var qa = document.getElementById('qa');
  var qaImg = document.getElementById('qa-img');
  var qaTitle = document.getElementById('qa-title');
  var qaPrice = document.getElementById('qa-price');
  var qaOpts = document.getElementById('qa-opts');
  var qaQty = document.getElementById('qa-qty');
  var qaAdd = document.getElementById('qa-add');
  var qaNote = document.getElementById('qa-note');
  var qaState = { p: null, sel: [], qty: 1 };
  var qaLastFocus = null;

  function qaVariant() {
    var p = qaState.p;
    if (!p) return null;
    for (var i = 0; i < p.variants.length; i++) {
      var v = p.variants[i], ok = true;
      for (var j = 0; j < qaState.sel.length; j++) {
        if (String(v.opts[j]) !== String(qaState.sel[j])) { ok = false; break; }
      }
      if (ok) return v;
    }
    return null;
  }

  // A value is offered only if some available variant carries it.
  function valueAvailable(p, idx, val) {
    for (var i = 0; i < p.variants.length; i++) {
      if (p.variants[i].available && String(p.variants[i].opts[idx]) === String(val)) return true;
    }
    return false;
  }

  function productByHandle(h) {
    if (!shop || !h) return null;
    for (var i = 0; i < shop.products.length; i++) if (shop.products[i].handle === h) return shop.products[i];
    return null;
  }

  /* The one-tick offer under the add button. Two-colour eyewear: the OTHER
     colour at the second-pair price. Anything else: the configured product's
     single variant (multi-variant targets are skipped — no blind size picks). */
  function pairHandles() {
    return String(shop.pairHandles || '').split(',').map(function (h) { return h.trim(); }).filter(Boolean);
  }
  // The other colour of a two-colour pair, or null.
  function pairOther(p) {
    var hs = pairHandles();
    if (hs.length !== 2 || !p) return null;
    var i = hs.indexOf(p.handle);
    if (i < 0) return null;
    return productByHandle(hs[1 - i]);
  }
  function firstAvail(p) {
    for (var i = 0; p && i < p.variants.length; i++) if (p.variants[i].available) return p.variants[i];
    return null;
  }

  function sheetUpsell(p, v) {
    if (!p) return null;
    var other = pairOther(p), ov = firstAvail(other);
    if (other && ov) {
      var colour = other.title.split('\u2014').pop().trim();
      return { id: ov.id, price: shop.pairPrice || ov.price,
        label: (shop.secondPairLabel || '{colour} pair').replace('{colour}', colour) };
    }
    if (p.second && Object.keys(p.second).length && v) {
      for (var i = 0; i < p.variants.length; i++) {
        var o = p.variants[i];
        if (o === v || !o.available) continue;
        var s = p.second[String(o.opts[0]).toUpperCase()];
        if (s && s.available) return { id: s.id, price: s.price,
          label: (shop.secondPairLabel || '{colour} pair').replace('{colour}', o.opts[0]) };
      }
      return null;
    }
    var t = productByHandle(shop.sheetUpsellHandle);
    // The bundle product already includes the gift — never offer it there too.
    var isBundle = shop.bundleHandle && p.handle === shop.bundleHandle;
    if (isBundle && t && t.handle === shop.bundleGiftHandle) t = null;
    if (!t || t === p) {
      // The configured product IS this one: fall back to the next product in the grid.
      t = null;
      for (var k = 0; k < shop.products.length; k++) {
        var c = shop.products[k];
        if (isBundle && c.handle === shop.bundleGiftHandle) continue;
        if (c !== p && c.variants.some(function (x) { return x.available; })) { t = c; break; }
      }
    }
    if (!t) return null;
    var av = t.variants.filter(function (x) { return x.available; });
    if (!av.length) return null;
    var name = t.title;
    if (t.variants.length > 1) name += ' \u00b7 ' + av[0].opts.join(' / ');   // e.g. "VENOM 001 · BLACK"
    return { id: av[0].id, price: av[0].price, label: (shop.sheetUpsellLabel || 'Add {title}').replace('{title}', name) };
  }

  var qaUp = document.getElementById('qa-up');
  var qaUpBox = document.getElementById('qa-up-box');
  var qaUpLbl = document.getElementById('qa-up-lbl');
  var qaUpPrice = document.getElementById('qa-up-price');
  var qaOffer = null;

  function qaPaint() {
    var p = qaState.p, v = qaVariant();
    qaOpts.innerHTML = '';
    var chart = document.getElementById('qa-chart');
    if (chart) chart.hidden = !p.options.some(function (o) { return o.name === (shop.chartOption || 'Size'); });
    var hand = document.getElementById('qa-hand');
    if (hand) hand.hidden = String(shop.handmadeHandles || '').split(',').map(function (h) { return h.trim(); }).indexOf(p.handle) < 0;
    var bundle = document.getElementById('qa-bundle');
    if (bundle) { var isB = shop.bundleHandle && p.handle === shop.bundleHandle; bundle.hidden = !isB; if (isB) document.getElementById('qa-bundle-t').textContent = shop.bundleNote || ''; }
    p.options.forEach(function (o, idx) {
      // "What you see is what you get": no pills for Default Title or a single choice.
      if (o.name === 'Title') return;
      var avail = o.values.filter(function (val) { return valueAvailable(p, idx, val); });
      if (avail.length < 2) return;
      var wrap = document.createElement('div');
      wrap.className = 'qa__opt';
      var lab = document.createElement('span');
      lab.className = 'lbl';
      lab.textContent = o.name;
      var pills = document.createElement('div');
      pills.className = 'qa__pills';
      pills.setAttribute('role', 'radiogroup');
      o.values.forEach(function (val) {
        if (!valueAvailable(p, idx, val)) return;   // ICE on the eyewear, etc.
        var b = document.createElement('button');
        b.type = 'button';
        b.className = 'qa__pill';
        b.setAttribute('role', 'radio');
        b.setAttribute('aria-checked', String(qaState.sel[idx]) === String(val) ? 'true' : 'false');
        b.textContent = val;
        b.addEventListener('click', function () { qaState.sel[idx] = val; qaPaint(); });
        pills.appendChild(b);
      });
      wrap.appendChild(lab); wrap.appendChild(pills);
      qaOpts.appendChild(wrap);
    });
    qaQty.textContent = qaState.qty;
    qaOffer = sheetUpsell(p, v);
    if (qaUp) {
      qaUp.hidden = !qaOffer;
      if (qaOffer) { qaUpLbl.textContent = qaOffer.label; qaUpPrice.textContent = money(qaOffer.price); }
    }
    if (v) {
      qaPrice.textContent = money(v.price * qaState.qty);
      if (v.image) qaImg.src = v.image;   // colour switch swaps the photo
      qaAdd.disabled = !v.available;
      qaAdd.textContent = v.available ? (shop.cta || 'Add to bag') : 'Sold out';
    } else {
      qaPrice.textContent = '';
      qaAdd.disabled = true;
      qaAdd.textContent = 'Unavailable';
    }
  }

  function qaKey(e) { if (e.key === 'Escape') closeQa(); }

  function openQa(p) {
    if (!qa || !p) return;
    qaState.p = p;
    qaState.qty = 1;
    qaState.sel = [];
    // Default to the first AVAILABLE variant's values.
    var first = null;
    for (var i = 0; i < p.variants.length; i++) { if (p.variants[i].available) { first = p.variants[i]; break; } }
    if (!first) first = p.variants[0];
    if (first) qaState.sel = first.opts.slice();
    qaTitle.textContent = p.title;
    // An empty src resolves to the page URL, so track the image separately.
    var src = (first && first.image) || p.image || '';
    if (src) qaImg.src = src; else qaImg.removeAttribute('src');
    qaImg.parentNode.hidden = !src;
    qaNote.textContent = '';
    if (qaUpBox) qaUpBox.checked = false;
    // Extra images for this product (theme-editor "Product images" block).
    var extra = document.getElementById('qa-extra');
    if (extra) {
      extra.innerHTML = '';
      var tpl = document.querySelector('template[data-gal="' + p.handle + '"]');
      var has = tpl && tpl.content && tpl.content.querySelector('img');
      if (has) extra.appendChild(tpl.content.cloneNode(true));
      extra.hidden = !has;
    }
    qaPaint();
    qaLastFocus = document.activeElement;
    lockScroll();
    qa.classList.add('is-open');
    qa.setAttribute('aria-hidden', 'false');
    document.addEventListener('keydown', qaKey);
    var c = qa.querySelector('.linkbtn'); if (c) c.focus();
  }

  function closeQa() {
    if (!qa) return;
    qa.classList.remove('is-open');
    qa.setAttribute('aria-hidden', 'true');
    unlockScroll();
    document.removeEventListener('keydown', qaKey);
    if (qaLastFocus && qaLastFocus.focus) qaLastFocus.focus();
  }

  if (qa) {
    qa.querySelectorAll('[data-qa-close]').forEach(function (b) { b.addEventListener('click', closeQa); });
    document.getElementById('qa-minus').addEventListener('click', function () {
      qaState.qty = Math.max(1, qaState.qty - 1); qaPaint();
    });
    document.getElementById('qa-plus').addEventListener('click', function () {
      qaState.qty = Math.min(10, qaState.qty + 1); qaPaint();
    });
    qaAdd.addEventListener('click', function () {
      var v = qaVariant();
      if (!v || !v.available || qaAdd.disabled) return;
      qaAdd.disabled = true;
      qaAdd.textContent = 'Adding…';
      var items = [{ id: v.id, quantity: qaState.qty }];
      if (qaOffer && qaUpBox && qaUpBox.checked) items.push({ id: qaOffer.id, quantity: 1 });
      // Free gift rides along with the bundle product (priced to $0 by the automatic discount).
      if (shop.bundleHandle && qaState.p && qaState.p.handle === shop.bundleHandle) {
        var gv = firstAvail(productByHandle(shop.bundleGiftHandle));
        if (gv) items.push({ id: gv.id, quantity: qaState.qty });
      }
      addToCart(items)
        .then(function () { return render(); })
        .then(function () { closeQa(); open(); qaAdd.disabled = false; qaAdd.textContent = shop.cta || 'Add to bag'; })
        .catch(function (err) {
          qaAdd.disabled = false;
          qaAdd.textContent = shop.cta || 'Add to bag';
          qaNote.textContent = (err && err.status === 422) ? 'That one just sold out.' : 'Something went wrong — try again.';
        });
    });
    document.querySelectorAll('[data-open]').forEach(function (card) {
      card.addEventListener('click', function () { openQa(productById(card.getAttribute('data-open'))); });
    });
  }

  /* ----- bag upsells ---------------------------------------------------- */
  var upsBox = document.getElementById('cart-ups');

  function upsellCandidates(c) {
    var out = [];
    if (!shop || !c) return out;
    var inVar = {}, inProd = {};
    c.items.forEach(function (it) { inVar[it.variant_id] = 1; inProd[it.product_id] = 1; });

    var inHandle = {};
    shop.products.forEach(function (p) { if (inProd[p.id]) inHandle[p.handle] = 1; });
    shop.products.forEach(function (p) {
      if (inProd[p.id]) return;
      // The gift is already riding with the bundle product.
      if (p.handle === shop.bundleGiftHandle && inHandle[shop.bundleHandle]) return;
      // Other colour of a pair in the bag: offer it at the second-pair price.
      var mate = pairOther(p);
      if (mate && inProd[mate.id]) {
        var pv = firstAvail(p);
        if (pv) out.push({ p: p, id: pv.id, price: shop.pairPrice || pv.price, title: p.title,
          sub: (shop.secondPairLabel || '{colour} pair').replace('{colour}', p.title.split('\u2014').pop().trim()),
          image: pv.image || p.image, direct: true });
        return;
      }
      var hasSecond = p.second && Object.keys(p.second).length;
      if (hasSecond) {
        var have = p.variants.filter(function (v) { return inVar[v.id]; });
        if (have.length) {
          // A pair is in the bag: offer the OTHER colour at the second-pair price.
          p.variants.forEach(function (v) {
            if (inVar[v.id] || !v.available) return;
            var s = p.second[String(v.opts[0]).toUpperCase()];
            if (!s || !s.available || inVar[s.id]) return;
            out.push({ p: p, id: s.id, price: s.price, title: p.title,
              sub: (shop.secondPairLabel || '{colour} pair').replace('{colour}', v.opts[0]),
              image: v.image || p.image, direct: true });
          });
          return;
        }
      }
      if (inProd[p.id]) return;
      var avail = p.variants.filter(function (v) { return v.available; });
      if (!avail.length) return;
      var direct = avail.length === 1;
      var sub = '';
      if (!direct && p.options.length) sub = 'Choose ' + p.options[0].name.toLowerCase();
      out.push({ p: p, id: avail[0].id, price: avail[0].price, title: p.title, sub: sub, image: avail[0].image || p.image, direct: direct });
    });
    out.sort(function (a, b) { return (b.sub && b.sub.indexOf('pair') > -1 ? 1 : 0) - (a.sub && a.sub.indexOf('pair') > -1 ? 1 : 0); });
    return out.slice(0, 3);
  }

  function renderUps(c) {
    if (!upsBox) return;
    var list = upsellCandidates(c);
    upsBox.innerHTML = '';
    upsBox.hidden = !list.length || !c.items.length;
    if (upsBox.hidden) return;
    var h = document.createElement('p');
    h.className = 'lbl ups__h';
    h.textContent = shop.upsellHeading || 'Add to your order';
    upsBox.appendChild(h);
    var row = document.createElement('div');
    row.className = 'ups';
    list.forEach(function (u) {
      var card = document.createElement('div');
      card.className = 'ups__card';
      var img = document.createElement('div');
      img.className = 'ups__img';
      if (u.image) { var im = document.createElement('img'); im.src = u.image; im.alt = ''; img.appendChild(im); }
      var mid = document.createElement('div');
      mid.className = 'ups__mid';
      var t = document.createElement('span'); t.className = 'ups__t'; t.textContent = u.title;
      var s = document.createElement('span'); s.className = 'lbl ups__s'; s.textContent = u.sub || '';
      var pr = document.createElement('span'); pr.className = 'ups__p'; pr.textContent = money(u.price);
      mid.appendChild(t); if (u.sub) mid.appendChild(s); mid.appendChild(pr);
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ups__add';
      b.textContent = u.direct ? '+ Add' : 'Pick';
      b.addEventListener('click', function () {
        if (u.direct) {
          b.disabled = true; b.textContent = '…';
          addToCart([{ id: u.id, quantity: 1 }])
            .then(function () { return render(); })
            .catch(function () { b.disabled = false; b.textContent = '+ Add'; });
        } else {
          close();
          openQa(u.p);
        }
      });
      card.appendChild(img); card.appendChild(mid); card.appendChild(b);
      row.appendChild(card);
    });
    upsBox.appendChild(row);
  }

  render();
})();
